import {
  JSNS,
  type Content,
  type JsonObject,
  type AppSpecificPasswordData,
  type AppSpecificPasswordsInfo,
  type EnableTwoFactorResult,
  type OAuthConsumer,
  type TrustedDevicesInfo,
  type TwoFactorInfo,
} from '@zimbra-webif/types';
import type { ZimbraClient } from '../client';

/** Subset of AuthResponse we surface; the raw response is also returned. */
export interface AuthResult {
  token: string | undefined;
  /** token lifetime in ms */
  lifetime?: number;
  /** configured skin (classic-client concept; informational) */
  skin?: string;
  /** trusted-device handshake outputs (persist for trusted-device login) */
  deviceId?: string;
  trustedToken?: string;
  trustLifetime?: number;
  csrfToken?: string;
  /** 2FA negotiation state (when the server demands a second factor) */
  twoFactor: TwoFactorInfo;
  raw: AuthResponseBody;
}

interface AuthResponseBody {
  authToken?: Array<Content> | string;
  lifetime?: number;
  trustLifetime?: number;
  skin?: Array<Content>;
  csrfToken?: Array<Content> | string;
  deviceId?: Array<Content> | string;
  trustedToken?: Array<Content> | string;
  twoFactorAuthRequired?: Array<Content<boolean>> | boolean;
  trustedDevicesEnabled?: Array<Content<boolean>> | boolean;
  resetPassword?: Array<Content<boolean>> | boolean;
  zimbraTwoFactorAuthMethodAllowed?: { method?: Array<Content> };
  zimbraTwoFactorAuthMethodEnabled?: { method?: Array<Content> };
  zimbraPrefPrimaryTwoFactorAuthMethod?: Array<Content> | string;
  zimbraPrefPasswordRecoveryAddress?: Array<Content> | string;
  [k: string]: unknown;
}

/** ChangePassword / ResetPassword echo a fresh token. */
export interface PasswordChangeResult {
  token?: string;
  lifetime?: number;
  csrfToken?: string;
  raw: JsonObject;
}

/** RecoverAccount operations (verified `RecoverAccountOperation` enum). */
export type RecoverAccountOp = 'getRecoveryAccount' | 'sendRecoveryCode' | 'sendRecoveryLink';

/** RecoverAccountResponse. */
export interface RecoverAccountResult {
  /** masked recovery address */
  recoveryAccount?: string;
  recoveryAttemptsLeft?: number;
  raw: JsonObject;
}

function content(value: unknown): string | undefined {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return (value[0] as Content | undefined)?._content;
  if (value && typeof value === 'object' && '_content' in value) {
    return String((value as Content)._content);
  }
  return undefined;
}

function boolContent(value: unknown): boolean | undefined {
  const raw = Array.isArray(value)
    ? (value[0] as Content<boolean> | undefined)?._content
    : value && typeof value === 'object' && '_content' in value
      ? (value as Content<boolean>)._content
      : value;
  if (raw === undefined || raw === null) return undefined;
  if (typeof raw === 'boolean') return raw;
  return raw === true || raw === 'true' || raw === 1 || raw === '1';
}

function methodList(wrapper: { method?: Array<Content> } | undefined): string[] | undefined {
  if (!wrapper?.method) return undefined;
  return wrapper.method.map((m) => m._content).filter((s): s is string => typeof s === 'string');
}

function extractToken(body: AuthResponseBody): string | undefined {
  return content(body.authToken);
}

function buildTwoFactor(body: AuthResponseBody): TwoFactorInfo {
  const info: TwoFactorInfo = {};
  const req = boolContent(body.twoFactorAuthRequired);
  if (req !== undefined) info.twoFactorAuthRequired = req;
  const td = boolContent(body.trustedDevicesEnabled);
  if (td !== undefined) info.trustedDevicesEnabled = td;
  const reset = boolContent(body.resetPassword);
  if (reset !== undefined) info.resetPassword = reset;
  const allowed = methodList(body.zimbraTwoFactorAuthMethodAllowed);
  if (allowed) info.methodAllowed = allowed;
  const enabled = methodList(body.zimbraTwoFactorAuthMethodEnabled);
  if (enabled) info.methodEnabled = enabled;
  const primary = content(body.zimbraPrefPrimaryTwoFactorAuthMethod);
  if (primary) info.primaryMethod = primary;
  const recovery = content(body.zimbraPrefPasswordRecoveryAddress);
  if (recovery) info.passwordRecoveryAddress = recovery;
  return info;
}

function toAuthResult(raw: AuthResponseBody): AuthResult {
  return {
    token: extractToken(raw),
    lifetime: raw.lifetime,
    trustLifetime: raw.trustLifetime,
    skin: raw.skin?.[0]?._content,
    deviceId: content(raw.deviceId),
    trustedToken: content(raw.trustedToken),
    csrfToken: content(raw.csrfToken),
    twoFactor: buildTwoFactor(raw),
    raw,
  };
}

function scratchCodes(body: { scratchCodes?: { scratchCode?: Array<Content> | Content } }): string[] {
  const sc = body.scratchCodes?.scratchCode;
  if (!sc) return [];
  const arr = Array.isArray(sc) ? sc : [sc];
  return arr.map((c) => c._content).filter((s): s is string => typeof s === 'string');
}

/** Parameters for the high-level {@link AuthApi.auth} entry point. */
export interface AuthParams {
  /** account selector value (email/name) */
  name: string;
  /** plaintext password (password grant) */
  password?: string;
  /** recovery code (forgot-password completion via Auth) */
  recoveryCode?: string;
  /** 2FA second factor */
  twoFactorCode?: string;
  /** trust this device after a successful 2FA */
  deviceTrusted?: boolean;
  /** ask the server to mint a deviceId */
  generateDeviceId?: boolean;
  /** existing trusted-device token */
  trustedDeviceToken?: string;
  /** auth-token grant (re-auth) */
  authToken?: string;
  /** JWT grant */
  jwtToken?: string;
  tokenType?: string;
  virtualHost?: string;
  requestedSkin?: string;
  persistAuthTokenCookie?: boolean;
  csrfTokenSecured?: boolean;
  /** request specific prefs echoed back in the response */
  prefs?: string[];
  /** request specific attrs echoed back in the response */
  attrs?: string[];
}

/**
 * Authentication, session lifecycle & the full account security surface
 * (urn:zimbraAccount), plus the four recovery/verify commands Zimbra placed
 * under urn:zimbraMail (flagged inline).
 *
 * One public method per SOAP command; see docs/features/account-prefs-security.md.
 */
export class AuthApi {
  constructor(private readonly client: ZimbraClient) {}

  /**
   * AuthRequest by account name + password. On success the returned token is
   * stored on the client for subsequent calls.
   *
   * PRESERVED public signature — do not change.
   */
  async byPassword(
    name: string,
    password: string,
    opts?: { tokenType?: string },
  ): Promise<AuthResult> {
    const raw = await this.client.call<AuthResponseBody>(JSNS.account, 'Auth', {
      account: { by: 'name', _content: name },
      password: { _content: password },
      ...(opts?.tokenType ? { tokenType: opts.tokenType } : {}),
    });
    const result = toAuthResult(raw);
    if (result.token) {
      this.client.setAuthToken(result.token);
    }
    return result;
  }

  /**
   * AuthRequest — full-featured login. Supports password, recovery-code,
   * existing-auth-token re-auth, JWT, the 2FA second factor + trusted-device
   * handshake, and requesting prefs/attrs inline. Stores any returned token.
   */
  async auth(params: AuthParams): Promise<AuthResult> {
    const body: JsonObject = { account: { by: 'name', _content: params.name } };
    if (params.password !== undefined) body.password = { _content: params.password };
    if (params.recoveryCode !== undefined) body.recoveryCode = { _content: params.recoveryCode };
    if (params.twoFactorCode !== undefined) body.twoFactorCode = { _content: params.twoFactorCode };
    if (params.authToken !== undefined) body.authToken = { _content: params.authToken };
    if (params.jwtToken !== undefined) body.jwtToken = { _content: params.jwtToken };
    if (params.trustedDeviceToken !== undefined) {
      body.trustedToken = { _content: params.trustedDeviceToken };
    }
    if (params.virtualHost !== undefined) body.virtualHost = { _content: params.virtualHost };
    if (params.requestedSkin !== undefined) body.requestedSkin = { _content: params.requestedSkin };
    if (params.deviceTrusted !== undefined) body.deviceTrusted = params.deviceTrusted;
    if (params.generateDeviceId !== undefined) body.generateDeviceId = params.generateDeviceId;
    if (params.tokenType !== undefined) body.tokenType = params.tokenType;
    if (params.persistAuthTokenCookie !== undefined) {
      body.persistAuthTokenCookie = params.persistAuthTokenCookie;
    }
    if (params.csrfTokenSecured !== undefined) body.csrfTokenSecured = params.csrfTokenSecured;
    if (params.prefs) body.prefs = { pref: params.prefs.map((name) => ({ name })) };
    if (params.attrs) body.attrs = { attr: params.attrs.map((name) => ({ name })) };

    const raw = await this.client.call<AuthResponseBody>(JSNS.account, 'Auth', body);
    const result = toAuthResult(raw);
    if (result.token) {
      this.client.setAuthToken(result.token);
    }
    return result;
  }

  /** AuthRequest using an existing auth token (re-auth). Stores the new token. */
  async authWithToken(token: string): Promise<AuthResult> {
    return this.auth({ name: '', authToken: token });
  }

  /**
   * AuthRequest completing a 2FA challenge with the second-factor code; the
   * preceding password Auth must have returned `twoFactorAuthRequired`.
   */
  async authTwoFactor(
    name: string,
    code: string,
    opts?: { trustDevice?: boolean; generateDeviceId?: boolean },
  ): Promise<AuthResult> {
    return this.auth({
      name,
      twoFactorCode: code,
      ...(opts?.trustDevice !== undefined ? { deviceTrusted: opts.trustDevice } : {}),
      ...(opts?.generateDeviceId !== undefined ? { generateDeviceId: opts.generateDeviceId } : {}),
    });
  }

  /** EndSession — invalidate the current auth token and clear it locally. */
  async endSession(): Promise<void> {
    await this.client.call(JSNS.account, 'EndSession', {});
    this.client.setAuthToken(undefined);
  }

  /**
   * EndSession with options: `logoff` clears the auth-token cookie, `all` ends
   * all of this user's sessions (`excludeCurrent` keeps the current one alive).
   * Clears the local token unless `excludeCurrent` was requested.
   */
  async endSessionWith(opts: {
    logoff?: boolean;
    all?: boolean;
    excludeCurrent?: boolean;
    sessionId?: string;
  }): Promise<void> {
    const params: JsonObject = {};
    if (opts.logoff !== undefined) params.logoff = opts.logoff;
    if (opts.all !== undefined) params.all = opts.all;
    if (opts.excludeCurrent !== undefined) params.excludeCurrent = opts.excludeCurrent;
    if (opts.sessionId !== undefined) params.sessionId = opts.sessionId;
    await this.client.call(JSNS.account, 'EndSession', params);
    if (!opts.excludeCurrent) {
      this.client.setAuthToken(undefined);
    }
  }

  /**
   * ChangePassword for the given account.
   *
   * PRESERVED public signature — do not change.
   */
  async changePassword(name: string, oldPassword: string, newPassword: string): Promise<void> {
    await this.client.call(JSNS.account, 'ChangePassword', {
      account: { by: 'name', _content: name },
      oldPassword: { _content: oldPassword },
      password: { _content: newPassword },
    });
  }

  /**
   * ChangePasswordRequest with full options. `dryRun` tests policy without
   * committing. Returns the fresh auth token (swap it in on success).
   */
  async changePasswordEx(opts: {
    account: string;
    oldPassword: string;
    newPassword: string;
    dryRun?: boolean;
    virtualHost?: string;
  }): Promise<PasswordChangeResult> {
    const body: JsonObject = {
      account: { by: 'name', _content: opts.account },
      oldPassword: { _content: opts.oldPassword },
      password: { _content: opts.newPassword },
    };
    if (opts.dryRun !== undefined) body.dryRun = opts.dryRun;
    if (opts.virtualHost !== undefined) body.virtualHost = { _content: opts.virtualHost };
    const raw = await this.client.call<JsonObject>(JSNS.account, 'ChangePassword', body);
    const token = content(raw.authToken);
    if (token && !opts.dryRun) this.client.setAuthToken(token);
    return { token, lifetime: raw.lifetime as number | undefined, csrfToken: content(raw.csrfToken), raw };
  }

  /**
   * ResetPasswordRequest — forced reset (no old password); authorized by the
   * constrained session token set after AuthResponse `resetPassword`.
   */
  async resetPassword(newPassword: string): Promise<PasswordChangeResult> {
    const raw = await this.client.call<JsonObject>(JSNS.account, 'ResetPassword', {
      password: { _content: newPassword },
    });
    const token = content(raw.authToken);
    if (token) this.client.setAuthToken(token);
    return { token, lifetime: raw.lifetime as number | undefined, csrfToken: content(raw.csrfToken), raw };
  }

  /* ---------------------------------------------------------------------- */
  /* Recovery / verify — urn:zimbraMail (NOT account) per Zimbra            */
  /* ---------------------------------------------------------------------- */

  /**
   * RecoverAccountRequest — pre-login forgot-password recovery.
   * NAMESPACE: urn:zimbraMail (Zimbra placed this under mail, not account).
   */
  async recoverAccount(opts: {
    op: RecoverAccountOp;
    email: string;
    channel?: string;
  }): Promise<RecoverAccountResult> {
    const params: JsonObject = { op: opts.op, email: opts.email };
    if (opts.channel !== undefined) params.channel = opts.channel;
    const raw = await this.client.call<JsonObject>(JSNS.mail, 'RecoverAccount', params);
    return {
      recoveryAccount: raw.recoveryAccount as string | undefined,
      recoveryAttemptsLeft: raw.recoveryAttemptsLeft as number | undefined,
      raw,
    };
  }

  /**
   * SetRecoveryAccountRequest — configure/verify the account recovery email.
   * NAMESPACE: urn:zimbraMail.
   *
   * `op` vocabulary (sendCode/validateCode/resendCode/reset) is ASSUMED — verify
   * vs live capture (doc Open question #1). Pass the exact server string.
   */
  async setRecoveryAccount(opts: {
    op: string; // ASSUMED enum — verify vs live capture
    recoveryAccount?: string;
    code?: string;
    channel?: string;
  }): Promise<JsonObject> {
    const params: JsonObject = { op: opts.op };
    if (opts.recoveryAccount !== undefined) params.recoveryAccount = opts.recoveryAccount;
    if (opts.code !== undefined) params.recoveryAccountVerificationCode = opts.code;
    if (opts.channel !== undefined) params.channel = opts.channel;
    return this.client.call(JSNS.mail, 'SetRecoveryAccount', params);
  }

  /**
   * SendVerificationCodeRequest — email a generic verification code to `address`.
   * NAMESPACE: urn:zimbraMail.
   */
  async sendVerificationCode(address: string): Promise<JsonObject> {
    return this.client.call(JSNS.mail, 'SendVerificationCode', { a: address });
  }

  /**
   * VerifyCodeRequest — check a verification code for `address`.
   * NAMESPACE: urn:zimbraMail. Returns true when the server reports success.
   */
  async verifyCode(address: string, code: string): Promise<boolean> {
    const raw = await this.client.call<JsonObject>(JSNS.mail, 'VerifyCode', { a: address, code });
    const success = boolContent(raw.success);
    // No success flag documented → treat a non-fault response as success.
    return success ?? true;
  }

  /* ---------------------------------------------------------------------- */
  /* Two-factor auth                                                         */
  /* ---------------------------------------------------------------------- */

  /**
   * EnableTwoFactorAuthRequest — enroll TOTP (or email) 2FA. The server returns
   * the shared `secret`, a fresh interim `authToken`, and the initial scratch
   * codes. Pass `code` (a current TOTP code) to finish enrollment in one step.
   */
  async enableTwoFactor(opts: {
    account: string;
    password?: string;
    authToken?: string;
    method?: string;
    email?: string;
    code?: string;
    csrfTokenSecured?: boolean;
  }): Promise<EnableTwoFactorResult> {
    const body: JsonObject = { name: { _content: opts.account } };
    if (opts.password !== undefined) body.password = { _content: opts.password };
    if (opts.authToken !== undefined) body.authToken = { _content: opts.authToken };
    if (opts.method !== undefined) body.method = { _content: opts.method };
    if (opts.email !== undefined) body.email = { _content: opts.email };
    if (opts.code !== undefined) body.twoFactorCode = { _content: opts.code };
    if (opts.csrfTokenSecured !== undefined) body.csrfTokenSecured = opts.csrfTokenSecured;
    const raw = await this.client.call<JsonObject>(JSNS.account, 'EnableTwoFactorAuth', body);
    const token = content(raw.authToken);
    if (token) this.client.setAuthToken(token);
    return {
      secret: content(raw.secret),
      authToken: token,
      scratchCodes: scratchCodes(raw),
      csrfToken: content(raw.csrfToken),
      raw,
    };
  }

  /** DisableTwoFactorAuthRequest — turn off 2FA (omit `method` to disable all). */
  async disableTwoFactor(method?: string): Promise<JsonObject> {
    const body: JsonObject = {};
    if (method !== undefined) body.method = { _content: method };
    return this.client.call(JSNS.account, 'DisableTwoFactorAuth', body);
  }

  /**
   * SendTwoFactorAuthCodeRequest — ask the server to email the OTP (email 2FA
   * method). `action` values (email/reset) are ASSUMED — verify vs live capture
   * (doc Open question #1). Returns the `<status>` text when present.
   */
  async sendTwoFactorCode(action?: string): Promise<string | undefined> {
    const body: JsonObject = {};
    if (action !== undefined) body.action = { _content: action }; // ASSUMED — verify vs live capture
    const raw = await this.client.call<JsonObject>(JSNS.account, 'SendTwoFactorAuthCode', body);
    return content(raw.status);
  }

  /** GenerateScratchCodesRequest — issue a fresh set (invalidates the old set). */
  async generateScratchCodes(): Promise<string[]> {
    const raw = await this.client.call<JsonObject>(JSNS.account, 'GenerateScratchCodes', {});
    return scratchCodes(raw);
  }

  /** GetScratchCodesRequest — list the remaining recovery codes. */
  async getScratchCodes(): Promise<string[]> {
    const raw = await this.client.call<JsonObject>(JSNS.account, 'GetScratchCodes', {});
    return scratchCodes(raw);
  }

  /* ---------------------------------------------------------------------- */
  /* Trusted devices                                                         */
  /* ---------------------------------------------------------------------- */

  /** GetTrustedDevicesRequest — this-device-trusted flag + other-device count. */
  async getTrustedDevices(): Promise<TrustedDevicesInfo> {
    const raw = await this.client.call<JsonObject>(JSNS.account, 'GetTrustedDevices', {});
    const info: TrustedDevicesInfo = {};
    if (raw.nOtherDevices !== undefined) info.nOtherDevices = Number(raw.nOtherDevices);
    const trusted = boolContent(raw.thisDeviceTrusted);
    if (trusted !== undefined) info.thisDeviceTrusted = trusted;
    return info;
  }

  /** RevokeTrustedDeviceRequest — forget THIS device (uses its session token). */
  async revokeThisDevice(): Promise<void> {
    await this.client.call(JSNS.account, 'RevokeTrustedDevice', {});
  }

  /** RevokeOtherTrustedDevicesRequest — revoke trust for all OTHER devices. */
  async revokeOtherDevices(): Promise<void> {
    await this.client.call(JSNS.account, 'RevokeOtherTrustedDevices', {});
  }

  /* ---------------------------------------------------------------------- */
  /* App-specific passwords                                                  */
  /* ---------------------------------------------------------------------- */

  /**
   * CreateAppSpecificPasswordRequest — issue a per-app password (shown once).
   * Returns the generated password from the response `pw` attr.
   */
  async createAppPassword(appName: string): Promise<string | undefined> {
    const raw = await this.client.call<JsonObject>(JSNS.account, 'CreateAppSpecificPassword', {
      appName,
    });
    return content(raw.pw) ?? (typeof raw.pw === 'string' ? raw.pw : undefined);
  }

  /** GetAppSpecificPasswordsRequest — list app passwords + the max allowed. */
  async getAppPasswords(): Promise<AppSpecificPasswordsInfo> {
    const raw = await this.client.call<JsonObject>(JSNS.account, 'GetAppSpecificPasswords', {});
    const wrapper = raw.appSpecificPasswords as { passwordData?: AppSpecificPasswordData[] } | undefined;
    const list = wrapper?.passwordData;
    const passwords = Array.isArray(list) ? list : list ? [list] : [];
    const max = raw.maxAppPasswords;
    return {
      passwords,
      ...(max !== undefined ? { maxAppPasswords: Number(content(max) ?? max) } : {}),
    };
  }

  /** RevokeAppSpecificPasswordRequest — revoke a single app password by name. */
  async revokeAppPassword(appName: string): Promise<void> {
    await this.client.call(JSNS.account, 'RevokeAppSpecificPassword', { appName });
  }

  /* ---------------------------------------------------------------------- */
  /* OAuth consumers (third-party connected apps)                            */
  /* ---------------------------------------------------------------------- */

  /** GetOAuthConsumersRequest — list third-party apps holding an OAuth token. */
  async getOAuthConsumers(): Promise<OAuthConsumer[]> {
    const raw = await this.client.call<{ OAuthConsumer?: OAuthConsumer[] | OAuthConsumer }>(
      JSNS.account,
      'GetOAuthConsumers',
      {},
    );
    const list = raw.OAuthConsumer;
    return Array.isArray(list) ? list : list ? [list] : [];
  }

  /** RevokeOAuthConsumerRequest — revoke an app's access token. */
  async revokeOAuthConsumer(accessToken: string): Promise<void> {
    await this.client.call(JSNS.account, 'RevokeOAuthConsumer', { accessToken });
  }
}
